import React from "react";
import styled, { css, keyframes, RuleSet } from "styled-components";
import { Tooltip } from "antd";
import {
  CheckOutlined,
  MinusOutlined,
  QuestionOutlined,
  SwapOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from "@ant-design/icons";

import {
  compassBordeaux,
  compassLightGreen,
  compassYellow,
  lightGreyText,
  lightGreys,
} from "../../../../styles";
import {
  AssessmentStateV2,
  ConfidenceV2,
} from "../../../../types/engagementAssessmentV2Types";

export type RingSize = "xs" | "sm" | "md" | "lg";

const DIAMETER: Record<RingSize, number> = { xs: 16, sm: 20, md: 26, lg: 44 };
const STROKE: Record<RingSize, number> = { xs: 1.5, sm: 1.75, md: 2, lg: 3 };
const GLYPH: Record<RingSize, number> = { xs: 7, sm: 8.5, md: 10, lg: 12.5 };

const Wrap = styled.span<{ $d: number }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $d }): number => $d}px;
  height: ${({ $d }): number => $d}px;
  flex-shrink: 0;
`;

const Svg = styled.svg`
  position: absolute;
  inset: 0;
  transform: rotate(-90deg);
`;

const Centre = styled.span<{ $tint: string; $glyph: number }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ $tint }): string => $tint};
  font-size: ${({ $glyph }): number => $glyph}px;
  line-height: 1;
  font-family: "ApercuMono", "Apercu", monospace;
`;

const clamp = (v: number): number => Math.max(0, Math.min(100, v));

const drawIn = (circumference: number): ReturnType<typeof keyframes> => keyframes`
  from { stroke-dashoffset: ${circumference}; }
`;

const Arc = styled.circle<{ $c: number }>`
  transition: stroke-dashoffset 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);
  ${({ $c }): RuleSet<object> => css`
    animation: ${drawIn($c)} 0.9s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

export const ScoreRing = ({
  value,
  tint,
  glyph,
  size = "sm",
  tooltip,
}: ScoreRingProps): JSX.Element => {
  const d = DIAMETER[size];
  const sw = STROKE[size];
  const r = (d - sw) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference * (1 - clamp(value) / 100);

  const badge = (
    <Wrap $d={d}>
      <Svg width={d} height={d} viewBox={`0 0 ${d} ${d}`} aria-hidden="true">
        <circle cx={d / 2} cy={d / 2} r={r} fill="none" stroke={lightGreys.borderLightGrey} strokeWidth={sw} />
        <Arc
          $c={circumference}
          cx={d / 2}
          cy={d / 2}
          r={r}
          fill="none"
          stroke={tint}
          strokeWidth={sw}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </Svg>
      <Centre $tint={size === "lg" ? tint : lightGreyText} $glyph={GLYPH[size]}>
        {glyph}
      </Centre>
    </Wrap>
  );

  return tooltip ? (
    <Tooltip title={tooltip} placement="top">
      {badge}
    </Tooltip>
  ) : (
    badge
  );
};

type ScoreRingProps = {
  value: number;
  tint: string;
  glyph: React.ReactNode;
  size?: RingSize;
  tooltip?: string;
};

const CONFIDENCE: Record<ConfidenceV2, { pct: number; tint: string; glyph: JSX.Element }> = {
  HIGH: { pct: 90, tint: compassLightGreen, glyph: <ThunderboltOutlined /> },
  MEDIUM: { pct: 65, tint: compassYellow, glyph: <MinusOutlined /> },
  LOW: { pct: 35, tint: lightGreyText, glyph: <QuestionOutlined /> },
};

export const confidencePct = (c: ConfidenceV2): number => CONFIDENCE[c].pct;

export const ConfidenceRing = ({
  confidence,
  size = "sm",
}: {
  confidence: ConfidenceV2;
  size?: RingSize;
}): JSX.Element => {
  const c = CONFIDENCE[confidence];
  const label = confidence.charAt(0) + confidence.slice(1).toLowerCase();
  return (
    <ScoreRing
      value={c.pct}
      tint={c.tint}
      glyph={c.glyph}
      size={size}
      tooltip={`${label} confidence · ${c.pct}%`}
    />
  );
};

const STATE: Record<AssessmentStateV2, { pct: number; tint: string; glyph: JSX.Element }> = {
  STRONG: { pct: 100, tint: compassLightGreen, glyph: <CheckOutlined /> },
  ADEQUATE: { pct: 75, tint: compassLightGreen, glyph: <CheckOutlined /> },
  MIXED: { pct: 50, tint: compassYellow, glyph: <SwapOutlined /> },
  WEAK: { pct: 25, tint: compassBordeaux, glyph: <WarningOutlined /> },
  NOT_RATABLE: { pct: 0, tint: lightGreyText, glyph: <MinusOutlined /> },
};

export const stateTint = (state: AssessmentStateV2): string => STATE[state].tint;
export const statePct = (state: AssessmentStateV2): number => STATE[state].pct;

export const StateRing = ({
  state,
  size = "sm",
}: {
  state: AssessmentStateV2;
  size?: RingSize;
}): JSX.Element => {
  const s = STATE[state];
  const label = state
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
  return (
    <ScoreRing
      value={s.pct}
      tint={s.tint}
      glyph={s.glyph}
      size={size}
      tooltip={`${label} · ${s.pct}/100`}
    />
  );
};
